import type { ReactNode } from 'react';
import { ArrowUpIcon } from './icons';

/**
 * CHAT-DUET (his pick, 2026-10-28): the chat box in the Duet look - ONE round box (27 px corners, at least 54 px tall,
 * 5 px inside, frosted, a soft float shadow) holding the controls on one row, with an optional strip above the row
 * (picked files). Used by every chat box in the app so they cannot drift apart.
 *
 * ⛔ The input keeps `text-base` below `sm`: iOS zooms the whole page when it focuses a field under 16 px (index.css
 *   forces 16 px on touch screens anyway); `sm:text-sm` keeps the computer's 14 px.
 */
export const CHAT_INPUT =
  'min-w-0 flex-1 border-0 bg-transparent px-1 py-2 text-base text-chat-ink placeholder:text-chat-hint focus:outline-none focus:ring-0 sm:text-sm';

/** A round 44 px control in the box (the +, the mic): grey ink, a faint tint on hover. */
export const ICON_BUTTON =
  'flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-chat-ink-2 transition-colors hover:bg-black/5 disabled:opacity-40';

/**
 * ⛔ The box shows the focus, not the input (it has no outline of its own): a soft Apex ring while anything in it holds
 *   the focus. Phase 1's iPhone fix relies on it - the top bar's Assistant pressed during "Loading..." focuses the box
 *   with no keyboard, and the cue is the box "in view and outlined" (Morning.tsx). A ring composes with the float shadow.
 */
export function ChatBox({ children, top, className = '' }: { children: ReactNode; top?: ReactNode; className?: string }) {
  return (
    <div
      data-testid="chat-box"
      className={`chat-glass shadow-chat-float flex min-h-[54px] min-w-0 flex-col rounded-[27px] p-[5px] focus-within:ring-2 focus-within:ring-primary-500/40 ${className}`}
    >
      {top ? <div data-chat-box-top className="flex min-w-0 flex-wrap gap-1 px-2 pb-1 pt-1">{top}</div> : null}
      <div className="flex min-w-0 items-center gap-1">{children}</div>
    </div>
  );
}

/**
 * The round black Send (an up-arrow). ⛔ Named "Send" whatever it draws - the rest of the app and its tests reach it
 * by that name. Dimmed to 30% exactly when it is off; `busy` draws a spinner and says so (`aria-busy`).
 */
export function SendButton({ disabled, onClick, type = 'button', busy = false }: {
  disabled: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  busy?: boolean;
}) {
  return (
    <button
      type={type}
      aria-label="Send"
      aria-busy={busy || undefined}
      disabled={disabled}
      onClick={onClick}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-chat-black text-white transition-opacity hover:opacity-90 disabled:opacity-30"
    >
      {busy
        ? <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
        : <ArrowUpIcon className="h-5 w-5" />}
    </button>
  );
}
